import React, { useState } from "react";
import { MailCheck, BadgeCheck, Loader2 } from "lucide-react";
import Modal from "@/components/chat/Modal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function VerifyEmailDialog({ onClose }) {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState(user.email_verified ? "done" : "start"); // start | code | done
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-verification");
      if (data.already) { setUser({ ...user, email_verified: true }); setStep("done"); }
      else { toast.success(t("verify_code_sent")); setStep("code"); }
    } catch (e) { toast.error("Failed to send code"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.trim().length < 4) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      setUser({ ...user, email_verified: true });
      setStep("done");
      toast.success(t("verify_success"));
    } catch (e) { toast.error(t("verify_invalid")); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={t("verify_email")} onClose={onClose}>
      <div className="flex flex-col items-center text-center" data-testid="verify-email-dialog">
        <div className="h-20 w-20 rounded-full grid place-items-center bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] mb-4">
          {step === "done" ? <BadgeCheck size={38} /> : <MailCheck size={36} />}
        </div>
        <p className="text-sm text-[var(--rc-muted)] mb-1">{user.email}</p>

        {step === "done" ? (
          <>
            <h4 className="font-heading text-2xl text-[var(--rc-gold)] mb-2">{t("verify_success")}</h4>
            <p className="text-sm text-[var(--rc-muted)]">{t("already_verified")}</p>
            <button onClick={onClose} data-testid="verify-close-btn" className="mt-6 w-full rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3">OK</button>
          </>
        ) : step === "start" ? (
          <>
            <p className="text-sm text-[var(--rc-muted)] mb-6 max-w-sm">{t("verify_intro")}</p>
            <button onClick={sendCode} disabled={busy} data-testid="verify-send-code-btn"
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3">
              {busy && <Loader2 size={16} className="animate-spin" />} {t("verify_send_code")}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-[var(--rc-muted)] mb-4 max-w-sm">{t("verify_enter_code")}</p>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder="000000" data-testid="verify-code-input"
              className="w-full text-center tracking-[0.5em] text-2xl font-semibold bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 focus:outline-none focus:border-[var(--rc-gold)]/60" />
            <button onClick={confirm} disabled={busy || code.length < 4} data-testid="verify-confirm-btn"
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
              {busy && <Loader2 size={16} className="animate-spin" />} {t("verify_confirm")}
            </button>
            <button onClick={sendCode} disabled={busy} data-testid="verify-resend-btn"
              className="mt-3 text-sm text-[var(--rc-gold)]">{t("verify_resend")}</button>
          </>
        )}
      </div>
    </Modal>
  );
}
